import '@glideapps/glide-data-grid/dist/index.css'
import * as React from 'react'
import { useInView } from 'react-intersection-observer'
import { Block, type ListBlock } from '@basehub/data/replicache/index'
import { useSelectedView } from './views/use-selected-view'
import { ListInputTableView } from './views/table'
import { ListBlockActions } from './actions'
import { ListInputGalleryView } from './views/gallery'
import { normalizeStringPath } from '@basehub/data/replicache/index'
import { ListInputInlineView } from './views/inline'
import { BlocksProps } from '..'
import { EditorModeProvider } from '../../editor-mode-provider'
import { HEADER_HEIGHT_WITH_BORDERS, ROW_HEIGHT } from './views/table/theme'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { UseListTableResult, useListTable } from './use-list-table'

export type ViewComponentProps = {
  block: ListBlock
  path: string
  fullView?: boolean
  variant: ListBlockInputProps['variant']
  listTable: UseListTableResult
}

const LazyView = React.memo(
  ({
    ViewComponent,
    block,
    path,
    fullView,
    variant,
    listTable,
  }: {
    listTable: UseListTableResult
    ViewComponent: React.ComponentType<ViewComponentProps>
    block: ListBlock
    path: string
    fullView?: boolean
    variant?: BlocksProps['variant']
  }) => {
    const { ref, inView } = useInView({
      threshold: 0,
      triggerOnce: true,
      rootMargin: '200px', // Start loading when item is 200px from viewport
    })

    const childrenLength = useWorkingTreeStore(
      (s) => s.tree?.getChildrenKeys(idToSourceBlockId(block)).length ?? 0
    )

    // Calculate minimum height for placeholder
    const [selectedView] = useSelectedView(block)
    const minHeight = React.useMemo(() => {
      return Math.max(
        HEADER_HEIGHT_WITH_BORDERS +
          Math.min(selectedView?.showOnFirstLoad ?? 10, childrenLength) *
            ROW_HEIGHT,
        HEADER_HEIGHT_WITH_BORDERS * 2
      )
    }, [childrenLength, selectedView?.showOnFirstLoad])

    return (
      <div ref={ref}>
        {inView ? (
          <ViewComponent
            block={block}
            path={path}
            fullView={fullView}
            variant={variant}
            listTable={listTable}
          />
        ) : (
          <div style={{ height: minHeight }} />
        )}
      </div>
    )
  }
)

export type ListBlockInputProps = {
  block: ListBlock
  parent: Block
  path: string
  fullView: boolean
  variant?: BlocksProps['variant']
}

export const ListBlockInput = ({
  block,
  parent,
  path: _path,
  fullView,
  variant = 'default',
}: ListBlockInputProps) => {
  const [selectedView] = useSelectedView(block)
  const listPath = normalizeStringPath({
    path: _path,
    edgeId: block.id,
    includeEdge: true,
    format: 'string',
  })
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const listTable = useListTable({
    block,
    treeManager,
  })

  return (
    <EditorModeProvider parentBlock={parent} block={block}>
      <ListBlockActions
        block={block}
        parent={parent}
        path={listPath}
        isFullView={fullView}
        shortcuts={{
          fullscreen: !fullView,
          createView: fullView,
          template: true,
          filter: variant === 'default',
          sort: variant === 'default',
        }}
      />

      {selectedView?.layout === 'table' ? (
        <LazyView
          listTable={listTable}
          ViewComponent={ListInputTableView}
          block={block}
          path={listPath}
          fullView={fullView}
          variant={variant}
        />
      ) : selectedView?.layout === 'gallery' ? (
        <LazyView
          listTable={listTable}
          ViewComponent={ListInputGalleryView}
          block={block}
          path={listPath}
          fullView={fullView}
        />
      ) : selectedView?.layout === 'inline' ? (
        <LazyView
          listTable={listTable}
          ViewComponent={ListInputInlineView}
          block={block}
          path={listPath}
          fullView={fullView}
        />
      ) : (
        <LazyView
          listTable={listTable}
          ViewComponent={ListInputTableView}
          block={block}
          path={listPath}
          fullView={fullView}
          variant={variant}
        />
      )}
    </EditorModeProvider>
  )
}
